import React, {useMemo} from "react";
import {ApexOptions} from "apexcharts";
import {Col, Tab, Tabs} from "react-bootstrap";
import Chart from "react-apexcharts";
import {OrderData} from "../../../types/admin";
import {ADMIN_LANG} from "../../../settings";

const OrderChart = ({optionsData}: { optionsData: OrderData }) => {
    const {options: primaryNodes, connections} = optionsData;

    // Memoized summary data computation
    const summaryData = useMemo(() => {
        return primaryNodes.map((primaryNode) => {
            const totalCorrect = connections
                .filter(conn => conn.option_id === primaryNode.option_id && conn.position === primaryNode.correct_position)
                .reduce((sum, conn) => sum + conn.amount, 0);

            const totalIncorrect = connections
                .filter(conn => conn.option_id === primaryNode.option_id && conn.position !== primaryNode.correct_position)
                .reduce((sum, conn) => sum + conn.amount, 0);

            return {
                label: primaryNode.label,
                correct: totalCorrect,
                wrong: totalIncorrect,
            };
        });
    }, [primaryNodes, connections]);

    // Memoized summary chart options
    const summaryOptions: ApexOptions = useMemo(() => ({
        chart: {
            type: "bar",
            height: 350,
            stacked: true,
            toolbar: {
                show: false,
            },
        },
        plotOptions: {
            bar: {
                horizontal: true,
            },
        },
        dataLabels: {
            enabled: false,
        },
        xaxis: {
            categories: summaryData.map(data => data.label),
        },
        colors: ["#28a745", "#dc3545"],
    }), [summaryData]);


    const summarySeries = useMemo(() => [
        {
            name: ADMIN_LANG.correct_positions,
            data: summaryData.map(data => data.correct),
        },
        {
            name: ADMIN_LANG.incorrect_positions,
            data: summaryData.map(data => data.wrong),
        },
    ], [summaryData]);


    const generatePositionTabs = useMemo(() => {
        return primaryNodes.map((primaryNode, primaryIndex) => {
            const connectionsFromPrimary = connections.filter(
                conn => conn.option_id === primaryNode.option_id
            );

            const allPossiblePositions = primaryNodes.map((_, positionIndex) => {
                const existingConnection = connectionsFromPrimary.find(
                    conn => conn.position === positionIndex + 1
                );

                return {
                    label: `${positionIndex + 1}. ${ADMIN_LANG.position}`,
                    value: existingConnection ? existingConnection.amount : 0,
                    color: existingConnection && positionIndex + 1 === primaryNode.correct_position
                        ? "#28a745"
                        : "#dc3545",
                };
            });

            const labels = allPossiblePositions.map(pos => pos.label);
            const values = allPossiblePositions.map(pos => pos.value);
            const colors = allPossiblePositions.map(pos => pos.color);

            const options: ApexOptions = {
                chart: {
                    type: "bar",
                    height: 350,
                    toolbar: {
                        show: false,
                    },
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        distributed: true,
                    },
                },
                colors: colors,
                dataLabels: {
                    enabled: false,
                },
                xaxis: {
                    categories: labels,
                },
                tooltip: {
                    y: {
                        formatter: (val: number) => `${val}`,
                    },
                },
            };

            const series = [
                {
                    name: `${primaryIndex + 1}. ${primaryNode.label}`,
                    data: values,
                },
            ];

            return (
                <Tab eventKey={primaryNode.option_id} title={`${primaryIndex + 1}. ${primaryNode.label}`}
                     key={primaryNode.option_id}>
                    <h3>{primaryIndex + 1}. {primaryNode.label} <span
                        className='text-lowercase'>{ADMIN_LANG.positions}</span></h3>
                    <Chart options={options} series={series} type="bar" height={350}/>
                </Tab>
            );
        });
    }, [primaryNodes, connections]);

    return (
        <Col xs={12} lg={6} className="mb-4 mb-lg-0">
            <div className="admin-card">
                {connections.length > 0 ? (
                    <Tabs defaultActiveKey="summary" id="order-tabs" className="chartTabs">
                        <Tab eventKey="summary" title={ADMIN_LANG.summary}>
                            <h3>{ADMIN_LANG.summary}</h3>
                            <Chart options={summaryOptions} series={summarySeries} type="bar" height={350}/>
                        </Tab>
                        {generatePositionTabs}
                    </Tabs>
                ) : (
                    <h3>{ADMIN_LANG.no_data}</h3>
                )}
            </div>
        </Col>
    );
};

export default OrderChart;